import useOutsideClick from '../../../../utils/useOutsideClick';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './InputSelector.module.scss';

import clx from 'classnames';
import SelectorMenu from '../SelectorMenu';
import CustomInput from '../../CustomInput';
import useWindowSize from '../../../../utils/useWindowSize';

const dismissKeyboard = () => {
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }
};

const InputSelector = ({
  value,
  newValue,
  options,
  placeholder,
  title,
  icon,
  withIcon = false,
  disabled,
  loading,
  error,
  isEmptyMessage,
  readOnly = false,
  acceptNewValue = false,
  clearNewValueOnSelect = true,
  openOnFocus = true,
  setValue: setValueComponent = () => {},
  setNewValue = () => {},
  number,
}) => {
  const dropdownRef = useRef(null);
  const sortedOptions = useMemo(
    () =>
      [...(options || [])].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || '')),
      ),
    [options],
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [filteredOption, setFilteredOption] = useState(sortedOptions);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const { isMobile, isTablet } = useWindowSize();
  const isTouch = isMobile || isTablet;
  const selectRef = useRef(null);

  const defaultValue = Symbol('default').toString();

  const selectedOptionName = (currentValue) =>
    sortedOptions.find((option) => String(option.value) === String(currentValue))?.name;

  const commitFreeText = (text, currentValue = value) => {
    if (!acceptNewValue) return;
    const next = text || '';
    const selectedName = selectedOptionName(currentValue);
    if (selectedName && next === selectedName) {
      setNewValue('');
      return;
    }
    setNewValue(next);
    if (currentValue !== '' && currentValue != null) {
      setValueComponent('');
    }
  };

  const setValue = (nextValue) => {
    const isDefault = nextValue === defaultValue;
    setValueComponent(isDefault ? '' : nextValue);
    if (isDefault) {
      setNewValue(searchString);
    } else if (clearNewValueOnSelect) {
      setNewValue('');
    }
    setIsDropdownOpen(false);
    // Touch: chiudi tastiera subito dopo la selezione (cliente / nota / poltrona).
    if (isTouch) {
      window.requestAnimationFrame(dismissKeyboard);
    }
  };

  const handleSearchChange = (next) => {
    setSearchString(next);
    commitFreeText(next);
  };

  useEffect(() => {
    selectRef.current?.scrollTo(0, 0);

    if (value && sortedOptions.find((option) => option.value === value)?.name === searchString)
      return setFilteredOption(sortedOptions);
    setFilteredOption(
      sortedOptions
        .filter((option) => {
          const query = String(searchString || '').trim().toLowerCase();
          if (!query) return true;
          const name = String(option?.name || '').toLowerCase();
          const phone = String(option?.phone ?? '').toLowerCase();
          const email = String(option?.email ?? '').toLowerCase();
          return name.includes(query) || phone.includes(query) || email.includes(query);
        })
        .concat(
          acceptNewValue && searchString ? [{ name: searchString, value: defaultValue }] : [],
        ),
    );
  }, [sortedOptions, searchString, value]);

  useEffect(() => {
    value &&
      setSearchString(sortedOptions.find((option) => option.value === value)?.name || newValue || '');
  }, [value, isDropdownOpen, sortedOptions]);

  useOutsideClick(dropdownRef, () => {
    if (searchString && !selectedOptionName(value)) {
      commitFreeText(searchString);
    }
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      if (isTouch) window.requestAnimationFrame(dismissKeyboard);
    }
  });

  const clearData = () => {
    setValue('');
    setSearchString('');
  };

  useEffect(() => {
    if (selectRef?.current?.outerHTML) {
      selectRef.current.onscroll = (e) => {
        if (e.target.scrollTop + e.target.offsetHeight === e.target.scrollHeight) {
          setVisibleOptions((prev) =>
            prev.concat(filteredOption.slice(prev.length, prev.length + 20)),
          );
        }
      };
    }
  }, [selectRef.current]);
  useEffect(() => {
    setVisibleOptions(filteredOption.slice(0, 100));
  }, [filteredOption]);
  return (
    <div
      ref={dropdownRef}
      className={clx(styles.selector_wrapper, {
        [styles['selector_wrapper_mobile']]: isMobile,
        [styles['selector_wrapper_close']]: !isDropdownOpen,
      })}
    >
      <CustomInput
        placeholder={placeholder}
        inputValue={searchString}
        setInputValue={handleSearchChange}
        loading={loading}
        onAction={() => setIsDropdownOpen(!isDropdownOpen)}
        onInputFocus={openOnFocus ? () => setIsDropdownOpen(true) : () => {}}
        title={title}
        clearData={clearData}
        withIcon={withIcon}
        icon={icon}
        disabled={disabled}
        error={error}
        height={isMobile ? '40px' : 'fit-content'}
        readOnly={readOnly}
        autoComplete="off"
        inputMode={isTouch && !readOnly ? 'search' : undefined}
        enterKeyHint={isTouch && !readOnly ? 'done' : undefined}
        onKeyDown={
          isTouch
            ? (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitFreeText(searchString);
                  dismissKeyboard();
                  setIsDropdownOpen(false);
                }
              }
            : undefined
        }
      />
      <div className={styles.dropdown__menu} ref={selectRef}>
        <SelectorMenu
          fullHeight={true}
          isMultiple={false}
          setIsDropdownOpen={() => setIsDropdownOpen(false)}
          options={visibleOptions}
          setValue={setValue}
          value={[value]}
          isEmptyMessage={isEmptyMessage}
          number={number}
        />
      </div>
    </div>
  );
};

export default InputSelector;
